// Bảng tin nội bộ: kiểu dữ liệu, hàm hiển thị dùng chung và kho dữ liệu giả ở localStorage.
// Bảng tin, đăng / sửa / xoá bài, ảnh, hẹn giờ, lưu / ẩn bài, lượt xem đã đọc từ API thật (socialApi.ts). Kho localStorage bên dưới
// chỉ còn dùng cho phần chưa có API (nhắc bật thông báo trình duyệt) và các test cũ.
import { useSyncExternalStore } from 'react';
import { htmlToText } from '../../utils/html';

export type Audience = 'organization';
export type FeedFilter = 'all' | 'mine' | 'saved' | 'recognition' | 'mentions';

export interface Me { id: string; name: string; avatar: string | null; isAdmin: boolean; subtitle?: string }

/** Người được @nhắc tên: id = mã nhân sự (staffs), userId = tài khoản đăng nhập của nhân sự đó (để biết “nhắc đến tôi”) */
export interface Mention { id: number | string; userId?: string | null; name: string }

/** Thông tin hiển thị của người đã thả cảm xúc / đã xem */
export interface UserCard { name: string; avatar: string | null }

export interface Comment {
  id: string;
  authorId: string;
  authorName: string;
  authorAvatar: string | null;
  content: string;
  createdAt: string;
  editedAt?: string;
  likes: string[];
  replies: Comment[];
  mentions?: Mention[];
  /** Bình luận từ API: số thích và quyền của người xem do API tính sẵn */
  likeCount?: number;
  viewer?: { liked: boolean; canEdit: boolean; canDelete: boolean };
}

/** Ảnh trả về từ API tải ảnh; width / height có thể null */
export interface PostImage { url: string; width: number | null; height: number | null }

export interface Recognition { people: { id: number | string; userId?: string | null; name: string; avatar?: string | null }[]; badge: string }

export interface Post {
  id: string;
  authorId: string;
  authorName: string;
  authorAvatar: string | null;
  /** Đơn vị hiển thị cạnh tên tác giả (vd. "Khơ Thị Group") */
  organization?: string;
  audiences: Audience[];
  kind: 'post' | 'recognition';
  title?: string;
  html: string;
  images: string[];
  /** API: ảnh kèm kích thước, giữ nguyên để gửi lại khi sửa bài */
  imageMeta?: PostImage[];
  recognition?: Recognition;
  sharedFrom?: string;
  createdAt: string;
  /** Thời điểm hiển thị lên bảng tin; lớn hơn hiện tại = bài hẹn giờ */
  publishAt: string;
  editedAt?: string;
  commentsLocked: boolean;
  views: number;
  viewedBy: string[];
  /** userId → emoji */
  reactions: Record<string, string>;
  comments: Comment[];
  mentions?: Mention[];
  /** Bài lấy từ API (socialApi.ts): số liệu tổng và quyền / trạng thái của người xem do API tính sẵn */
  stats?: { reactions: number; reactionTop: { emoji: string; count: number }[]; comments: number };
  viewer?: { reaction: string | null; saved: boolean; muted: boolean; canEdit: boolean; canDelete: boolean };
  /** Đã tải danh sách bình luận từ API */
  commentsLoaded?: boolean;
  /** Bài gốc API trả kèm khi đây là bài chia sẻ */
  sharedPost?: Post | null;
  /** API: bài hẹn giờ chưa tới giờ (chỉ tác giả thấy) */
  scheduled?: boolean;
}

export interface Prefs { saved: string[]; hidden: string[]; muted: string[]; notiPromptDismissed?: boolean }

interface State { v: 1; posts: Post[]; prefs: Record<string, Prefs>; users: Record<string, UserCard> }

export const REACTIONS = [
  { emoji: '👍', label: 'Thích' },
  { emoji: '👏', label: 'Vỗ tay' },
  { emoji: '😆', label: 'Haha' },
  { emoji: '🎉', label: 'Chúc mừng' },
  { emoji: '😢', label: 'Buồn' },
  { emoji: '❤️', label: 'Yêu thích' },
];

export const BADGES = [
  { key: 'fire', emoji: '🔥', label: 'Nhiệt huyết' },
  { key: 'team', emoji: '🤝', label: 'Tinh thần đồng đội' },
  { key: 'star', emoji: '⭐', label: 'Xuất sắc' },
  { key: 'idea', emoji: '💡', label: 'Sáng tạo' },
  { key: 'care', emoji: '💖', label: 'Tận tâm với khách' },
];
export const badgeOf = (key?: string) => BADGES.find(b => b.key === key) ?? BADGES[0];

export const AUDIENCE_LABEL: Record<Audience, string> = { organization: 'Toàn tổ chức' };

export const PAGE_SIZE = 10;
const KEY = 'social_feed_v1';
const EMPTY_PREFS: Prefs = { saved: [], hidden: [], muted: [] };

// ---------------------------------------------------------------------------
// Người dùng hiện tại (RefreshAuth lưu thông tin /be/profile vào localStorage "user")
// ---------------------------------------------------------------------------
export function currentUser(): Me {
  let u: any = {};
  try { u = JSON.parse(localStorage.getItem('user') || '{}') || {}; } catch { u = {}; }
  const id = u.user_id ?? u.info?.id ?? u.id ?? 'guest';
  return {
    id: String(id),
    name: u.fullname || u.info?.fullname || u.info?.name || 'Tôi',
    avatar: u.info?.avatar || u.avatar || null,
    isAdmin: Number(u.type ?? localStorage.getItem('userType')) === 1,
    subtitle: u.info?.position || u.info?.staff?.position || u.email || u.info?.email || '',
  };
}

// ---------------------------------------------------------------------------
// Hàm thuần (có test)
// ---------------------------------------------------------------------------
export const uid = () => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;

export const isScheduled = (p: Post, now: number) => new Date(p.publishAt).getTime() > now;

/** Chuẩn hoá để tìm không phân biệt hoa/thường và dấu tiếng Việt ("Trâm" ~ "tram", "Đức" ~ "duc") */
export const normalize = (s: string) =>
  String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/đ/g, 'd').replace(/Đ/g, 'D').toLowerCase().replace(/\s+/g, ' ').trim();

const allComments = (list: Comment[]): Comment[] => list.flatMap(c => [c, ...allComments(c.replies)]);

/** Nội dung dùng để tìm: tiêu đề, nội dung, tác giả, người được ghi nhận / nhắc tên, bình luận (nếu người xem được thấy) */
export function searchText(p: Post, me: Me): string {
  const comments = p.commentsLocked && !canSeeLockedComments(p, me) ? [] : allComments(p.comments);
  return normalize([
    p.title, htmlToText(p.html), p.authorName, p.organization,
    ...(p.recognition?.people.map(x => x.name) || []),
    ...(p.mentions?.map(x => x.name) || []),
    ...comments.flatMap(c => [c.authorName, c.content]),
  ].filter(Boolean).join(' \n '));
}

/** Mọi từ khoá đều phải khớp với đầu một từ trong bài (thứ tự bất kỳ): "tram" khớp "Trâm", "gi" không khớp "gian" giữa câu */
export function matchesQuery(p: Post, q: string, me: Me): boolean {
  const words = normalize(q).split(/[^a-z0-9]+/).filter(Boolean);
  if (!words.length) return true;
  const tokens = searchText(p, me).split(/[^a-z0-9]+/).filter(Boolean);
  return words.every(w => tokens.some(t => t.startsWith(w)));
}

/** Bài có nhắc đến tôi: được @ trong bài / bình luận, hoặc được ghi nhận */
export function mentionsMe(p: Post, me: Me): boolean {
  const hit = (list?: { userId?: string | null }[]) => !!list?.some(x => x.userId != null && String(x.userId) === me.id);
  const comments = p.commentsLocked && !canSeeLockedComments(p, me) ? [] : allComments(p.comments);
  return hit(p.mentions) || hit(p.recognition?.people) || comments.some(c => hit(c.mentions));
}

/** Bài hiển thị trên bảng tin theo bộ lọc; bài hẹn giờ chỉ tác giả thấy ở mục riêng */
export function filterFeed(posts: Post[], filter: FeedFilter, me: Me, prefs: Prefs, now: number, keepHidden: string[] = [], q = ''): Post[] {
  return posts
    .filter(p => !isScheduled(p, now))
    .filter(p => !prefs.hidden.includes(p.id) || keepHidden.includes(p.id))
    .filter(p => filter === 'all'
      || (filter === 'mine' && p.authorId === me.id)
      || (filter === 'saved' && prefs.saved.includes(p.id))
      || (filter === 'recognition' && p.kind === 'recognition')
      || (filter === 'mentions' && mentionsMe(p, me)))
    .filter(p => matchesQuery(p, q, me))
    .sort((a, b) => new Date(b.publishAt).getTime() - new Date(a.publishAt).getTime());
}

/** Lấy danh sách người được @nhắc từ HTML trình soạn thảo (thẻ span.mention có data-mention-id) */
export function extractMentions(html: string): Mention[] {
  if (!html) return [];
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const seen = new Map<string, Mention>();
  doc.querySelectorAll('[data-mention-id]').forEach(el => {
    const id = el.getAttribute('data-mention-id') || '';
    if (!id || seen.has(id)) return;
    seen.set(id, { id: Number.isFinite(Number(id)) ? Number(id) : id, userId: el.getAttribute('data-user-id') || null, name: (el.textContent || '').replace(/^@/, '').trim() });
  });
  return Array.from(seen.values());
}

/** Bình luận: chỉ giữ người được nhắc mà "@Tên" vẫn còn trong nội dung */
export const mentionsInText = (text: string, picked: Mention[]) => {
  const seen = new Set<string>();
  return picked.filter(m => text.includes(`@${m.name}`) && !seen.has(String(m.id)) && seen.add(String(m.id)));
};

/** Đoạn "@chữ" ngay trước con trỏ (chưa có khoảng trắng) → đang gõ để nhắc tên */
export const mentionQueryAt = (text: string, caret: number) => {
  const m = /(^|\s)@([^\s@]{0,30})$/.exec(text.slice(0, caret));
  return m ? { start: caret - m[2].length - 1, q: m[2] } : null;
};

/** Danh sách người đã thả cảm xúc (lọc theo emoji nếu có) và người đã xem, kèm tên lấy từ danh bạ */
export function reactorsOf(p: Post, users: Record<string, UserCard>, emoji?: string) {
  return Object.entries(p.reactions)
    .filter(([, e]) => !emoji || e === emoji)
    .map(([id, e]) => ({ id, emoji: e, ...(users[id] || { name: 'Đồng nghiệp', avatar: null }) }));
}
export function viewersOf(p: Post, users: Record<string, UserCard>) {
  const list = p.viewedBy.map(id => ({ id, ...(users[id] || { name: 'Đồng nghiệp', avatar: null }) }));
  // lượt xem trước khi có danh sách người xem (dữ liệu cũ / mẫu)
  return { list, earlier: Math.max(0, p.views - list.length) };
}

export const scheduledOf = (posts: Post[], me: Me, now: number) =>
  posts.filter(p => p.authorId === me.id && isScheduled(p, now)).sort((a, b) => a.publishAt.localeCompare(b.publishAt));

export function reactionSummary(p: Post, meId: string) {
  if (p.stats) return { total: p.stats.reactions, top: p.stats.reactionTop, mine: p.viewer?.reaction ?? null };
  const counts = new Map<string, number>();
  Object.values(p.reactions).forEach(e => counts.set(e, (counts.get(e) || 0) + 1));
  const top = Array.from(counts.entries()).sort((a, b) => b[1] - a[1]).map(([emoji, count]) => ({ emoji, count }));
  return { total: Object.keys(p.reactions).length, top, mine: p.reactions[meId] ?? null };
}

export const commentCount = (p: Post) => p.stats?.comments ?? p.comments.reduce((s, c) => s + 1 + c.replies.length, 0);

/** Bảng xếp hạng người được ghi nhận nhiều nhất */
export function recognitionBoard(posts: Post[], now: number, limit = 5) {
  const map = new Map<string, { name: string; avatar?: string | null; count: number }>();
  posts.filter(p => p.kind === 'recognition' && !isScheduled(p, now)).forEach(p =>
    p.recognition?.people.forEach(x => {
      const k = String(x.id ?? x.name);
      const cur = map.get(k) || { name: x.name, avatar: x.avatar, count: 0 };
      map.set(k, { ...cur, count: cur.count + 1 });
    }));
  return Array.from(map.values()).sort((a, b) => b.count - a.count || a.name.localeCompare(b.name)).slice(0, limit);
}

export const canEditPost = (p: Post, me: Me) => p.viewer?.canEdit ?? p.authorId === me.id;
export const canDeletePost = (p: Post, me: Me) => p.viewer?.canDelete ?? (p.authorId === me.id || me.isAdmin);
export const canModifyComment = (c: Comment, me: Me) => (c.viewer ? c.viewer.canEdit || c.viewer.canDelete : c.authorId === me.id || me.isAdmin);
/** Bài đã "chặn và ẩn bình luận": chỉ tác giả và quản trị còn thấy bình luận cũ */
export const canSeeLockedComments = (p: Post, me: Me) => p.authorId === me.id || me.isAdmin;
/** Số bình luận người xem được thấy (bài đã ẩn bình luận thì là 0 với người khác) */
export const visibleCommentCount = (p: Post, me: Me) => (p.stats ? p.stats.comments : p.commentsLocked && !canSeeLockedComments(p, me) ? 0 : commentCount(p));

/** "Vừa xong", "5 phút", "Hôm qua lúc 09:00", "24 tháng 3 lúc 08:15", "24 tháng 3, 2025" */
export function timeAgo(iso: string, now = Date.now()): string {
  const d = new Date(iso);
  const t = d.getTime();
  if (!Number.isFinite(t)) return '';
  const diff = Math.max(0, now - t) / 1000;
  const hm = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  if (diff < 60) return 'Vừa xong';
  if (diff < 3600) return `${Math.floor(diff / 60)} phút`;
  const n = new Date(now);
  const startToday = new Date(n.getFullYear(), n.getMonth(), n.getDate()).getTime();
  if (t >= startToday) return `${Math.floor(diff / 3600)} giờ`;
  if (t >= startToday - 86400000) return `Hôm qua lúc ${hm}`;
  if (d.getFullYear() === n.getFullYear()) return `${d.getDate()} tháng ${d.getMonth() + 1} lúc ${hm}`;
  return `${d.getDate()} tháng ${d.getMonth() + 1}, ${d.getFullYear()}`;
}

/** Lọc HTML từ trình soạn thảo trước khi hiển thị: bỏ script/iframe/sự kiện on*, link javascript: */
export function sanitizeHtml(html: string): string {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
  const root = doc.body.firstElementChild as HTMLElement;
  root.querySelectorAll('script,style,iframe,object,embed,link,meta,form,input,button,textarea,select,base,frame,frameset,svg,math').forEach(el => el.remove());
  root.querySelectorAll('*').forEach(el => {
    Array.from(el.attributes).forEach(a => {
      const name = a.name.toLowerCase();
      const val = a.value.trim().toLowerCase().replace(/\s+/g, '');
      if (name.startsWith('on') || name === 'srcdoc') el.removeAttribute(a.name);
      else if ((name === 'href' || name === 'src' || name === 'xlink:href' || name === 'action' || name === 'formaction')
        && !(/^(https?:|mailto:|tel:|#|\/)/.test(val) || (name === 'src' && /^data:image\/(png|jpe?g|gif|webp);/.test(val)))) el.removeAttribute(a.name);
      else if (name === 'style' && /expression|javascript:|url\(/.test(val)) el.removeAttribute(a.name);
    });
    if (el.tagName === 'A') { el.setAttribute('target', '_blank'); el.setAttribute('rel', 'noopener noreferrer'); }
  });
  return root.innerHTML;
}

export { htmlToText };

// ---------------------------------------------------------------------------
// Dữ liệu mẫu ban đầu (giữ hai bài giới thiệu sẵn có của màn hình)
// ---------------------------------------------------------------------------
// Tên mẫu cho những người đã thả cảm xúc ở bài mẫu
const SEED_NAMES = ['Ngọc Hân', 'Minh Thư', 'Bảo Ngọc', 'Thanh Tâm', 'Quốc Bảo', 'Phương Anh', 'Gia Huy', 'Thu Trang', 'Hoàng Nam', 'Khánh Linh', 'Đức Anh', 'Mai Phương', 'Tuấn Kiệt', 'Hải Yến', 'Thảo Vy', 'Minh Khoa', 'Lan Chi', 'Trọng Nhân'];
const SEED_EXTRA = ['Diệu Linh', 'Văn Toàn', 'Kim Ngân', 'Hữu Phước', 'Ánh Dương', 'Thiên Ân'];
const seedDirectory = (): Record<string, UserCard> => ({
  ...Object.fromEntries(SEED_NAMES.map((name, i) => [`seed-${i + 1}`, { name, avatar: null }])),
  ...Object.fromEntries(SEED_EXTRA.map((name, i) => [`seed-h${i}`, { name, avatar: null }])),
});

const seedUsers = (n: number, emoji: string) => Object.fromEntries(Array.from({ length: n }, (_, i) => [`seed-${i + 1}`, emoji]));
const seedComment = (id: string, name: string, content: string, createdAt: string, likes: number): Comment => ({
  id, authorId: `seed-${id}`, authorName: name, authorAvatar: null,
  content, createdAt, likes: Array.from({ length: likes }, (_, i) => `seed-${i + 1}`), replies: [],
});

function seed(): State {
  return {
    v: 1,
    prefs: {},
    users: seedDirectory(),
    posts: [
      {
        id: 'seed-2', authorId: 'seed-bantt', authorName: 'Ban Truyền Thông', authorAvatar: null, organization: 'Khơ Thị Group',
        audiences: ['organization'], kind: 'post',
        title: '🚀 KHƠ THỊ CHÍNH THỨC KHAI TRƯƠNG CHI NHÁNH QUẬN 7 - KHÔNG GIAN LÀM ĐẸP ĐẲNG CẤP QUỐC TẾ',
        html: '<p>Sáng nay, Viện thẩm mỹ Khơ Thị chi nhánh Quận 7 đã chính thức cắt băng khánh thành và đi vào hoạt động đón khách. Cảm ơn toàn thể các bộ phận từ Truyền thông, Vận hành đến đội ngũ Bác sĩ và KTV đã nỗ lực hết mình trong thời gian qua! 🎉</p>'
          + '<div class="bg-teal-900 h-64 relative flex flex-col items-center justify-center p-6 text-white text-center rounded-lg mt-3"><i class="fa-solid fa-clinic-medical text-6xl text-teal-300 mb-3"></i><div class="text-2xl font-extrabold tracking-wide">KHƠ THỊ QUẬN 7 - FLAGSHIP CLINIC</div><p class="text-sm text-teal-200 mt-1">Đỉnh cao công nghệ thẩm mỹ không xâm lấn</p></div>',
        images: [], createdAt: '2026-10-08T09:00:00+07:00', publishAt: '2026-10-08T09:00:00+07:00', commentsLocked: false,
        views: 48, viewedBy: [], reactions: { ...seedUsers(18, '👍'), ...Object.fromEntries(Array.from({ length: 6 }, (_, i) => [`seed-h${i}`, '🎉'])) },
        comments: [seedComment('c21', 'Dr. Nam (Chuyên môn)', 'Cơ sở vật chất quá đỉnh! Chúc chi nhánh mới khách lúc nào cũng nườm nượp nha anh em ơi 🚀', '2026-10-08T10:15:00+07:00', 0)],
      },
      {
        id: 'seed-1', authorId: 'seed-cs', authorName: 'Customer Support', authorAvatar: null, organization: 'Buso',
        audiences: ['organization'], kind: 'post',
        title: '5 cách để nâng cao hiệu quả khi hợp tác chéo giữa các phòng ban',
        html: '<p>Điều gì góp phần tạo nên một môi trường đỉnh cao khi làm việc đan xen giữa các phòng ban với nhau? Sẽ thế nào nếu mọi người trong các phòng ban khác nhau không thể làm việc cùng nhau hiệu quả?</p>'
          + '<div class="grid grid-cols-2 gap-1 mt-3 rounded-lg overflow-hidden">'
          + '<div class="bg-[#7c4dff] aspect-[4/3] relative flex flex-col p-5 text-white overflow-hidden"><div class="text-[10px] font-bold opacity-70 mb-2">01 / 05</div><div class="text-[17px] font-bold leading-tight w-2/3">Lựa chọn công cụ kết nối hoàn hảo</div></div>'
          + '<div class="bg-[#f08a00] aspect-[4/3] relative flex flex-col p-5 text-white overflow-hidden"><div class="text-[10px] font-bold opacity-70 mb-2">02 / 05</div><div class="text-[17px] font-bold leading-tight w-4/5">Thiết lập chính sách chung về văn hóa làm việc của toàn đội</div></div>'
          + '<div class="bg-[#0091ff] aspect-[21/9] relative flex flex-col p-4 text-white overflow-hidden"><div class="text-[10px] font-bold opacity-70 mb-1">03 / 05</div><div class="text-[13px] font-bold leading-tight">Cung cấp các dữ liệu cần thiết cho mọi phòng ban</div></div>'
          + '<div class="bg-[#00c853] aspect-[21/9] relative flex flex-col p-4 text-white overflow-hidden"><div class="text-[10px] font-bold opacity-70 mb-1">04 / 05</div><div class="text-[13px] font-bold leading-tight">Ghi nhận quá trình làm việc hơn là kết quả</div></div>'
          + '</div>',
        images: [], createdAt: '2025-03-24T08:00:00+07:00', publishAt: '2025-03-24T08:00:00+07:00', commentsLocked: false,
        views: 13, viewedBy: [], reactions: {},
        comments: [
          seedComment('c11', 'Thanh Mai (Sales)', 'Rất chuẩn luôn ạ! Từ khi áp dụng quy trình đẩy số liệu chung trên hệ thống, team Sales và CSKH phối hợp chốt lịch khách mượt hơn hẳn, không còn bị miss thông tin nữa 👏', '2025-03-24T10:00:00+07:00', 5),
          seedComment('c12', 'Quang Huy', 'Phòng Marketing sẽ triển khai thêm tool nhắc việc tự động cho các dự án sắp tới nhé mọi người.', '2025-03-24T11:15:00+07:00', 0),
        ],
      },
      {
        id: 'seed-3', authorId: 'seed-mydung', authorName: 'Mỹ Dung (QLCN)', authorAvatar: null, organization: 'Khơ Thị Group',
        audiences: ['organization'], kind: 'recognition',
        html: '<p>Cảm ơn hai bạn đã hỗ trợ khách xuyên trưa trong ngày cao điểm, khách rất hài lòng!</p>',
        recognition: { people: [{ id: 'ktv-tramanh', name: 'KTV Trâm Anh' }, { id: 'ktv-baochau', name: 'KTV Bảo Châu' }], badge: 'fire' },
        images: [], createdAt: '2026-10-07T17:30:00+07:00', publishAt: '2026-10-07T17:30:00+07:00', commentsLocked: false,
        views: 21, viewedBy: [], reactions: seedUsers(9, '👏'), comments: [],
      },
    ],
  };
}

// ---------------------------------------------------------------------------
// Kho dữ liệu + đồng bộ giữa các component / tab trình duyệt
// ---------------------------------------------------------------------------
let state: State | null = null;
const listeners = new Set<() => void>();

function load(): State {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const s = JSON.parse(raw);
      if (s?.v === 1 && Array.isArray(s.posts)) return { v: 1, posts: s.posts, prefs: s.prefs || {}, users: { ...seedDirectory(), ...(s.users || {}) } };
    }
  } catch { /* dữ liệu hỏng → dùng dữ liệu mẫu */ }
  return seed();
}

const get = (): State => (state ??= load());
const emit = () => listeners.forEach(l => l());

export class StorageFullError extends Error {}

function commit(next: State) {
  try {
    localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    throw new StorageFullError('Bộ nhớ trình duyệt đã đầy, hãy bớt ảnh hoặc xoá bớt bài cũ.');
  }
  state = next;
  emit();
}

const mutatePosts = (fn: (posts: Post[]) => Post[], me?: Me) => {
  const s = get();
  // ghi lại tên / ảnh của người vừa tương tác để hiện trong danh sách “ai đã xem / thả cảm xúc”
  const users = me && (s.users[me.id]?.name !== me.name || s.users[me.id]?.avatar !== me.avatar)
    ? { ...s.users, [me.id]: { name: me.name, avatar: me.avatar } } : s.users;
  commit({ ...s, posts: fn(s.posts), users });
};
const mutatePost = (id: string, fn: (p: Post) => Post, me?: Me) => mutatePosts(ps => ps.map(p => (p.id === id ? fn(p) : p)), me);
const mapComments = (list: Comment[], id: string, fn: (c: Comment) => Comment | null): Comment[] =>
  list.flatMap(c => {
    if (c.id === id) { const r = fn(c); return r ? [r] : []; }
    return [{ ...c, replies: mapComments(c.replies, id, fn) }];
  });

if (typeof window !== 'undefined') {
  window.addEventListener('storage', e => { if (e.key === KEY) { state = load(); emit(); } });
}

const subscribe = (l: () => void) => { listeners.add(l); return () => { listeners.delete(l); }; };

export function useSocialState() {
  return useSyncExternalStore(subscribe, get);
}

export const prefsOf = (s: State, meId: string): Prefs => ({ ...EMPTY_PREFS, ...(s.prefs[meId] || {}) });

function setPrefs(meId: string, fn: (p: Prefs) => Prefs) {
  const s = get();
  commit({ ...s, prefs: { ...s.prefs, [meId]: fn(prefsOf(s, meId)) } });
}
const toggleIn = (list: string[], id: string) => (list.includes(id) ? list.filter(x => x !== id) : [...list, id]);

// ---------------------------------------------------------------------------
// Thao tác
// ---------------------------------------------------------------------------
export interface PostInput {
  title?: string;
  html: string;
  images: string[];
  audiences: Audience[];
  commentsLocked: boolean;
  publishAt?: string | null;
  kind?: Post['kind'];
  recognition?: Recognition;
  sharedFrom?: string;
  mentions?: Mention[];
}

export const socialApi = {
  createPost(me: Me, input: PostInput): Post {
    const now = new Date().toISOString();
    const post: Post = {
      id: uid(), authorId: me.id, authorName: me.name, authorAvatar: me.avatar,
      audiences: input.audiences.length ? input.audiences : ['organization'], kind: input.kind ?? 'post',
      title: input.title?.trim() || undefined, html: input.html, images: input.images,
      recognition: input.recognition, sharedFrom: input.sharedFrom, mentions: input.mentions,
      createdAt: now, publishAt: input.publishAt || now, commentsLocked: input.commentsLocked,
      views: 0, viewedBy: [], reactions: {}, comments: [],
    };
    mutatePosts(ps => [post, ...ps], me);
    return post;
  },
  updatePost(id: string, input: Partial<PostInput>) {
    mutatePost(id, p => ({ ...p, ...input, title: input.title !== undefined ? input.title.trim() || undefined : p.title, publishAt: input.publishAt || p.publishAt, editedAt: new Date().toISOString() }));
  },
  deletePost(id: string) { mutatePosts(ps => ps.filter(p => p.id !== id)); },
  publishNow(id: string) { mutatePost(id, p => ({ ...p, publishAt: new Date().toISOString() })); },
  react(id: string, me: Me, emoji: string | null) {
    mutatePost(id, p => {
      const reactions = { ...p.reactions };
      if (emoji) reactions[me.id] = emoji; else delete reactions[me.id];
      return { ...p, reactions };
    }, me);
  },
  markViewed(id: string, me: Me) {
    const p = get().posts.find(x => x.id === id);
    if (!p || p.viewedBy.includes(me.id)) return;
    try { mutatePost(id, x => ({ ...x, views: x.views + 1, viewedBy: [...x.viewedBy, me.id] }), me); } catch { /* bỏ qua khi bộ nhớ đầy */ }
  },
  setCommentsLocked(id: string, locked: boolean) { mutatePost(id, p => ({ ...p, commentsLocked: locked })); },
  addComment(postId: string, me: Me, content: string, parentId?: string, mentions: Mention[] = []) {
    const c: Comment = { id: uid(), authorId: me.id, authorName: me.name, authorAvatar: me.avatar, content: content.trim(), createdAt: new Date().toISOString(), likes: [], replies: [], mentions: mentionsInText(content, mentions) };
    mutatePost(postId, p => ({
      ...p,
      comments: parentId ? mapComments(p.comments, parentId, x => ({ ...x, replies: [...x.replies, c] })) : [...p.comments, c],
    }), me);
    return c;
  },
  editComment(postId: string, commentId: string, content: string) {
    mutatePost(postId, p => ({ ...p, comments: mapComments(p.comments, commentId, c => ({ ...c, content: content.trim(), mentions: mentionsInText(content, c.mentions || []), editedAt: new Date().toISOString() })) }));
  },
  deleteComment(postId: string, commentId: string) {
    mutatePost(postId, p => ({ ...p, comments: mapComments(p.comments, commentId, () => null) }));
  },
  toggleCommentLike(postId: string, commentId: string, meId: string) {
    mutatePost(postId, p => ({ ...p, comments: mapComments(p.comments, commentId, c => ({ ...c, likes: toggleIn(c.likes, meId) })) }));
  },
  toggleSaved(meId: string, id: string) { setPrefs(meId, p => ({ ...p, saved: toggleIn(p.saved, id) })); },
  toggleHidden(meId: string, id: string) { setPrefs(meId, p => ({ ...p, hidden: toggleIn(p.hidden, id) })); },
  toggleMuted(meId: string, id: string) { setPrefs(meId, p => ({ ...p, muted: toggleIn(p.muted, id) })); },
  dismissNotiPrompt(meId: string) { setPrefs(meId, p => ({ ...p, notiPromptDismissed: true })); },
};

/** Thu nhỏ ảnh trước khi lưu (cạnh dài tối đa 1280px, JPEG) để không đầy bộ nhớ */
export function compressImage(file: File, max = 1280, quality = 0.78): Promise<string> {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith('image/')) { reject(new Error('Chỉ hỗ trợ tệp ảnh')); return; }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, max / Math.max(img.width, img.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      const ctx = canvas.getContext('2d');
      if (!ctx) { URL.revokeObjectURL(url); reject(new Error('Không xử lý được ảnh')); return; }
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL('image/jpeg', quality));
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Không đọc được ảnh')); };
    img.src = url;
  });
}
